<script setup lang="ts">
import { computed, ref } from 'vue'
import { PModalDialog, PTextBody2, toast } from '@previo/design-system'
import { $t, downloadFile, type DownloadableFile } from '@previo/core'
import { FileShareResultType } from '~/components/file-share-dialog/types/FileShareResultType'
import { shareFile, toShareableFile } from '~/components/file-share-dialog/utils/shareFile'

const pendingFile = ref<File | null>(null)

const isOpen = computed<boolean>({
  get: () => pendingFile.value !== null,
  set: (value) => {
    if (!value) {
      dismiss()
    }
  },
})

const fileName = computed<string>(() => pendingFile.value?.name ?? '')

const share = async (file: DownloadableFile): Promise<void> => {
  const shareable = toShareableFile(file)
  const result = await shareFile(shareable)

  if (result === FileShareResultType.NEEDS_GESTURE) {
    pendingFile.value = shareable
  } else if (result === FileShareResultType.UNSUPPORTED) {
    await downloadFile(file)
  }
}

const sharePending = (): void => {
  const file = pendingFile.value
  pendingFile.value = null

  if (file !== null) {
    shareFile(file).catch((e) => toast.error(e))
  }
}

const dismiss = (): void => {
  pendingFile.value = null
}

defineExpose({ share })
</script>

<template>
  <p-modal-dialog
    v-model="isOpen"
    :title="$t('File is ready')"
    :primary-btn="$t('Share / save')"
    :tertiary-btn="$t('Cancel')"
    cy="file-share"
    @primary="sharePending"
    @tertiary="dismiss"
  >
    <p-text-body2 :text="fileName" />
  </p-modal-dialog>
</template>
